import React, { useEffect, useState } from 'react';
import { CaretRightOutlined, CloseOutlined } from '@ant-design/icons';
import { Alert, Button, Col, Collapse, Divider, Form, Row, Select } from 'antd';
import ResCol from 'components/forms/ResCol';
import useCookieHandler from 'core/useCookieHandler';
import { FormItemWrap, api, configManager } from 'react-structure-admin';
import { formartOnlyNumber, formatCurrency } from 'util/formatters';
import Iugu from 'util/iugu';
import CreditCardInfo from './CreditCardInfo';
import { useDispatch } from 'react-redux';
import { getResume } from 'stores/subscriptions/subscriptionActions';
import InputCoupon from './InputCoupon';
import PaymentPlanValue from './PaymentPlanValue';

const PaymentPlan = ({
  loading,
  onSubmit,
  onCancel,
  allowCoupon = true,
  checkout,
  loadingCheckout
}) => {
  const { plan } = checkout;
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [couponEnabled, setCouponEnabled] = useState(false);
  const config = configManager.getConfig();
  const { cookies, setCookie, removeCookie } = useCookieHandler([
    'cp',
    'planId'
  ]);

  const validateCreditCard = ({
    number,
    expirationDate,
    cardholderName,
    securityCode,
    installments = null,
    couponCode
  }) => {
    const month = expirationDate.substr(0, 2);
    const year = expirationDate.substr(3, 6);
    const [firstName, ...lastNames] = cardholderName.split(' ');
    const lastName = lastNames.join(' ');

    const cc = Iugu.CreditCard(
      formartOnlyNumber(number),
      month,
      year,
      firstName,
      lastName,
      securityCode
    );

    if (cc.valid()) {
      if (config.iuguUseJsTokenization) {
        Iugu.createPaymentToken(cc, (response) => {
          onSubmit({
            couponCode,
            paymentMethod: {
              creditCard: {
                token: response.id,
                holder: {
                  name: cardholderName
                }
              },
              installments
            }
          });
        });
      } else {
        onSubmit({
          couponCode,
          paymentMethod: {
            creditCard: {
              number: formartOnlyNumber(number),
              securityCode: securityCode,
              month: month,
              year: year,
              holder: {
                name: cardholderName
              }
            },
            installments
          }
        });
      }
    }
  };

  const handleFinish = (values) => {
    if (checkout.amount > 0) {
      validateCreditCard(values);
    } else {
      onSubmit({
        couponCode: checkout.coupon?.code
      });
    }
  };

  const setMessageErrorCouponCode = (message) => {
    form.setFields([
      {
        name: 'couponCode',
        errors: [message]
      }
    ]);
  };

  const removeCoupon = () => {
    form.setFields([{ name: 'couponCode', value: '' }]);
    handleChangeResume(null);
  };

  const handleChangeResume = (couponCode) => {
    dispatch(getResume({ planId: plan?.id, couponCode }));
  };

  const applyCoupon = (couponCode) => {
    if (!couponCode) {
      setMessageErrorCouponCode('Prencha corretamente o código promocional');
      return;
    }

    form.setFields([{ name: 'couponCode', value: couponCode }]);

    handleChangeResume(couponCode);
  };

  const handleApplyCoupon = () => {
    const { couponCode } = form.getFieldsValue();
    applyCoupon(couponCode);
  };

  const checkIfCouponIsEnabled = () => {
    api
      .fetch('coupons/has-coupon-enabled', { planId: plan.id })
      .then(({ data }) => {
        if (data?.result) {
          setCouponEnabled(data?.result);
        }
      });
  };

  const installmentsData = [];

  for (let parcel = 1; parcel <= 10; parcel += 1) {
    installmentsData.push(parcel);
  }

  useEffect(() => {
    checkIfCouponIsEnabled();
  }, []);

  useEffect(() => {
    if (couponEnabled) {
      if (cookies.cp) {
        applyCoupon(cookies.cp);
      }
    }

    if (cookies.planId) {
      removeCookie('planId');
    }
  }, [cookies, couponEnabled]);

  const isLoading = loading;

  return (
    <Form
      {...config.layout.form}
      name="payments"
      className="gx-h-100"
      form={form}
      validateTrigger="onBlur"
      initialValues={{ installments: 1 }}
      onFinish={handleFinish}
    >
      <Row className="gx-h-100">
        <ResCol span={checkout.amount > 0 ? 12 : 24} className="gx-h-100">
          <div className="gx-h-100 gx-flex-column gx-justify-content-between">
            <div>
              <Divider orientation="left">Resumo da assinatura</Divider>
              <div className="gx-card-plan-list gx-pb-0 gx-pt-0">
                <div className="gx-pb-0">
                  <ul>
                    <div className="gx-fs-xl gx-font-weight-semi-bold gx-mb-3">
                      {plan.name}
                    </div>
                    {plan.features
                      .filter((c, index) => index < 3)
                      .map((feature, index) => (
                        <li key={feature.id}>
                          <React.Fragment key={feature.description}>
                            <span>{feature.description}</span>
                          </React.Fragment>
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
              {checkout?.errors?.length > 0 && (
                <Alert
                  className="gx-ml-2"
                  message={checkout.errors.map(({ message }) => (
                    <div>{message}</div>
                  ))}
                  type="error"
                />
              )}
              {allowCoupon && couponEnabled && (
                <div>
                  <Collapse
                    className="gx-bg-white gx-paymentplan-collapse"
                    bordered={false}
                    defaultActiveKey={cookies.cp ? '1' : undefined}
                    expandIcon={({ isActive }) => (
                      <CaretRightOutlined rotate={isActive ? 90 : 0} />
                    )}
                  >
                    <Collapse.Panel
                      key="1"
                      header="Eu tenho um código promocional"
                    >
                      <FormItemWrap name="couponCode" whitespace autoFocus>
                        <InputCoupon
                          onSubmit={handleApplyCoupon}
                          loading={loadingCheckout}
                        />
                      </FormItemWrap>

                      {checkout.coupon && (
                        <Row className="no-margin no-padding" justify="start">
                          <Col>{checkout?.coupon?.code}</Col>
                          <Col>
                            <CloseOutlined
                              className="gx-pointer gx-text-red"
                              onClick={removeCoupon}
                            />
                          </Col>
                        </Row>
                      )}
                    </Collapse.Panel>
                  </Collapse>
                </div>
              )}
            </div>

            <PaymentPlanValue plan={plan} checkout={checkout} />
          </div>
        </ResCol>
        {checkout.amount > 0 && (
          <ResCol span={12}>
            <Divider orientation="left">Pagamento</Divider>
            <FormItemWrap>
              <CreditCardInfo />
            </FormItemWrap>
            {plan.frequency === 'yearly' ? (
              <FormItemWrap label="Parcelamento" name="installments" required>
                <Select
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.props.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {installmentsData.map((c) => (
                    <Select.Option key={c.toString()} value={c}>
                      {`${c}x ${formatCurrency(checkout?.amount / c)}`}
                    </Select.Option>
                  ))}
                </Select>
              </FormItemWrap>
            ) : null}
          </ResCol>
        )}
      </Row>
      <div className="gx-paymentplan-footer">
        <Button onClick={onCancel} disabled={isLoading}>
          Voltar
        </Button>
        <Button
          type="primary"
          onClick={form.submit}
          loading={isLoading}
          disabled={isLoading}
        >
          Confirmar pagamento
        </Button>
      </div>
    </Form>
  );
};

export default PaymentPlan;
